import { useState, useEffect } from "react";
import api from "@/lib/api";
import { X, Loader2, TrendingUp, TrendingDown, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * A/B-tests the persona's raw resume vs Sisyphean's tailored resume against the
 * same job using a Greenhouse/Workday/Lever/Taleo-style ATS prompt.
 * Only mounted on test_fixture applications.
 */
export default function AtsCompareModal({ jobId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.post(`/admin/test/ats-compare/${jobId}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || "ATS engine failed. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, jobId, data]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !loading) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="ats-compare-modal"
      className="fixed inset-0 z-[130] flex items-start justify-center pt-8 px-4 pb-12 bg-background/90 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-6xl p-6 md:p-8 my-4">
        <button
          data-testid="ats-compare-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="terminal-label text-primary mb-2">[ ATS.A/B.TEST ]</div>
        <h2 className="font-display text-2xl uppercase font-bold tracking-tight mb-2">
          Sisyphean vs. raw resume
        </h2>
        <p className="text-xs text-muted-foreground mb-6 leading-relaxed max-w-2xl">
          Both versions are scored by an ATS engine modeled after Greenhouse, Workday, Lever, and Taleo - the systems used by ~99% of Fortune 500 companies. Same job, same candidate, only the resume + materials differ.
        </p>

        {loading && (
          <div className="text-center py-20" data-testid="ats-compare-loading">
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-3" />
            <div className="terminal-label text-muted-foreground">Running both arms through the ATS engine…</div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono mt-1">~15-25 seconds</p>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 border border-destructive/40 bg-destructive/5 text-destructive text-sm font-mono">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            {error}
          </div>
        )}

        {data && !loading && (
          <div data-testid="ats-compare-result">
            {/* Hero delta bar */}
            <DeltaBanner delta={data.delta} candidateName={data.candidate_name} jobTitle={data.job.title} company={data.job.company} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <Arm label={data.raw.label} arm={data.raw} testId="ats-arm-raw" tone="muted" />
              <Arm label={data.optimized.label} arm={data.optimized} testId="ats-arm-optimized" tone="primary" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DeltaBanner({ delta, candidateName, jobTitle, company }) {
  const wins = delta > 0;
  const tied = delta === 0;
  const Icon = wins ? TrendingUp : tied ? Check : TrendingDown;
  const color = wins ? "text-accent-green" : tied ? "text-foreground" : "text-destructive";
  const label = wins ? `+${delta} pts with Sisyphean` : tied ? "Tie" : `${delta} pts (Sisyphean lower)`;
  return (
    <div className="panel p-5 flex items-center justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono mb-1">
          {candidateName} → {jobTitle} · {company}
        </div>
        <div className={`flex items-center gap-2 ${color}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
          <span className="serif-mod text-3xl tracking-tight" style={{ fontWeight: 500 }} data-testid="ats-delta-label">{label}</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
        Positive means Sisyphean's tailored materials moved the candidate further up the ATS ranking against this specific job.
      </p>
    </div>
  );
}

function recommendationStyle(rec) {
  const map = {
    "Strong Hire":  { bg: "bg-accent-green/15",  border: "border-accent-green/60",  text: "text-accent-green",  icon: "✦" },
    "Lean Hire":    { bg: "bg-accent-green/10",  border: "border-accent-green/40",  text: "text-accent-green",  icon: "◆" },
    "On the Fence": { bg: "bg-accent-amber/15",  border: "border-accent-amber/60",  text: "text-accent-amber",  icon: "◇" },
    "Lean No Hire": { bg: "bg-destructive/10",   border: "border-destructive/40",   text: "text-destructive",   icon: "▼" },
    "No Hire":      { bg: "bg-destructive/15",   border: "border-destructive/60",   text: "text-destructive",   icon: "✕" },
  };
  return map[rec] || map["On the Fence"];
}

function Arm({ label, arm, testId, tone }) {
  const { eval: e } = arm;
  const recStyle = recommendationStyle(e.hire_recommendation);
  const armBorder = tone === "primary" ? "border-primary/40" : "border-foreground/15";
  return (
    <div className={`panel p-5 ${armBorder}`} data-testid={testId} style={tone === "primary" ? { backgroundColor: "hsl(var(--primary) / 0.04)" } : undefined}>
      <div className={`terminal-label mb-3 ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`}>{label}</div>

      <div className="flex items-center gap-4 mb-5">
        <div className="serif-mod text-6xl tracking-[-0.04em] leading-none" style={{ fontWeight: 500 }} data-testid={`${testId}-score`}>
          {e.overall_score}
        </div>
        <div className="flex-1">
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${recStyle.border} ${recStyle.bg} ${recStyle.text} text-[10px] uppercase tracking-[0.18em] font-mono mb-2`}>
            <span>{recStyle.icon}</span> {e.hire_recommendation}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.2em] font-mono ${e.ats_pass ? "text-accent-green" : "text-destructive"}`}>
            {e.ats_pass ? "✓ Passes filter" : "✗ Filtered out"} · {e.hire_confidence}% confidence
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground/80 leading-relaxed italic mb-5 pl-3 border-l-2 border-foreground/20">
        "{e.recruiter_summary}"
      </p>

      <div className="space-y-2 mb-5">
        {Object.entries(e.score_breakdown).map(([key, val]) => (
          <div key={key}>
            <div className="flex items-center justify-between text-[11px] font-mono mb-1">
              <span className="text-foreground/80 uppercase tracking-wide">{key.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground tabular-nums">{val.score} · ×{val.weight}%</span>
            </div>
            <div className="h-1 bg-surface relative">
              <div
                className="absolute inset-y-0 left-0"
                style={{ width: `${val.score}%`, backgroundColor: barColor(val.score) }}
              />
            </div>
          </div>
        ))}
      </div>

      {e.knockout_flags?.length > 0 && (
        <div className="p-3 border border-destructive/40 bg-destructive/5 text-[11px] text-destructive mb-4 font-mono">
          <div className="uppercase tracking-[0.15em] mb-1">⚠ Knockout disqualifiers</div>
          {e.knockout_flags.map((f, i) => <div key={i}>• {f}</div>)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {e.strengths?.length > 0 && (
          <div>
            <div className="terminal-label text-accent-green mb-2">✓ Strengths</div>
            <ul className="text-[11px] text-foreground/80 leading-relaxed space-y-1">
              {e.strengths.map((s, i) => <li key={i}>· {s}</li>)}
            </ul>
          </div>
        )}
        {e.gaps?.length > 0 && (
          <div>
            <div className="terminal-label text-accent-amber mb-2">✗ Gaps</div>
            <ul className="text-[11px] text-foreground/80 leading-relaxed space-y-1">
              {e.gaps.map((g, i) => <li key={i}>· {g}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 mt-4">
        <div>
          <div className="terminal-label text-muted-foreground mb-2">Matched keywords</div>
          <div className="flex flex-wrap gap-1">
            {(e.score_breakdown.keyword_match.matched || []).map((k, i) => (
              <span key={i} className="text-[10px] font-mono px-2 py-0.5 bg-accent-green/10 text-accent-green border border-accent-green/30">{k}</span>
            ))}
          </div>
        </div>
        <div>
          <div className="terminal-label text-muted-foreground mb-2">Missing critical</div>
          <div className="flex flex-wrap gap-1">
            {(e.score_breakdown.keyword_match.missing_critical || []).length === 0
              ? <span className="text-[10px] text-muted-foreground font-mono">None - full coverage</span>
              : (e.score_breakdown.keyword_match.missing_critical || []).map((k, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-0.5 bg-destructive/10 text-destructive border border-destructive/30">{k}</span>
                ))}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-[11px] font-mono">
        <span className="text-muted-foreground uppercase tracking-wide">Next action</span>
        <span className="text-foreground">{e.next_action}</span>
      </div>
    </div>
  );
}

function barColor(score) {
  if (score >= 80) return "#22c55e";
  if (score >= 65) return "#eab308";
  if (score >= 50) return "#f97316";
  return "#ef4444";
}
